<template>
  <div class="roulette-monitor-wrapper">
    <!-- Main Card Container in Pure Light Theme -->
    <div
      class="roulette-card bg-white text-slate-800 rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
    >
      <!-- Card Header -->
      <div
        class="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white"
      >
        <!-- Left: Title & Live indicator -->
        <div class="flex items-center gap-3.5">
          <div>
            <div class="flex items-center gap-2">
              <h2
                class="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0"
              >
                Roulette Live History
              </h2>
              <span class="live-badge">
                <span class="pulse-ring"></span>
                LIVE
              </span>
            </div>
            <p class="text-xs text-slate-500 m-0 mt-0.5">
              Real-time extraction feed &bull;
              <strong class="text-slate-700 font-semibold">{{
                filteredNumbers.length
              }}</strong>
              spins displayed
            </p>
          </div>
        </div>

        <!-- Right: Actions & View Switcher -->
        <div class="flex flex-wrap items-stretch gap-2.5">
          <!-- View Switcher Tabs -->
          <div class="view-switcher-group">
            <button
              @click="activeView = 'grid'"
              :class="['switch-btn', activeView === 'grid' ? 'active' : '']"
              title="All Spins Grid Matrix"
            >
              <i class="fa-solid fa-table-cells text-xs"></i>
              <span>Grid</span>
            </button>
            <button
              @click="activeView = 'stats'"
              :class="['switch-btn', activeView === 'stats' ? 'active' : '']"
              title="Detailed Statistics & Heatmap"
            >
              <i class="fa-solid fa-chart-pie text-xs"></i>
              <span>Statistics</span>
            </button>
          </div>

          <!-- Total Pill -->
          <div
            class="bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-xl text-xs font-bold text-slate-600 hidden md:flex items-center gap-1.5"
          >
            <i class="fa-solid fa-layer-group text-slate-400 text-[11px]"></i>
            <span>{{ numbersList.length }} Total</span>
          </div>

          <!-- Refresh Button -->
          <button
            @click="handleManualRefresh"
            :disabled="isLoading || isRefreshing"
            class="refresh-btn"
            title="Refresh extractions"
          >
            <i
              class="fa-solid fa-arrows-rotate text-xs transition-transform"
              :class="{
                'animate-spin text-emerald-600': isRefreshing || isLoading,
              }"
            ></i>
          </button>
        </div>
      </div>

      <!-- Quick Statistics Banner (Always visible at glance) -->
      <div
        v-if="!isLoading && !isRefreshing && numbersList.length > 0"
        class="p-4 bg-slate-50/70 border-b border-slate-100"
      >
        <div class="flex flex-wrap lg:flex-nowrap items-stretch gap-3">
          <!-- Latest Number Spotlight -->
          <div
            v-if="latestItem"
            @click="openRoundDetail(latestItem)"
            class="flex-1 min-w-[220px] bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between shadow-xs cursor-pointer hover:border-emerald-300 transition-all"
            title="Click to view round details"
          >
            <div class="flex items-center gap-3">
              <div
                v-if="latestItem"
                :class="[
                  'roulette-chip chip-hero',
                  getChipColorClass(latestItem.multiplier),
                ]"
              >
                {{ latestItem.multiplier }}
              </div>
              <div>
                <div
                  class="text-[10px] font-extrabold uppercase tracking-wider text-emerald-600 flex items-center gap-1.5"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"
                  ></span>
                  Latest Result
                </div>
                <div class="text-xs font-bold text-slate-900 mt-0.5">
                  {{ getNumberDescriptor(latestItem?.multiplier) }}
                </div>
                <div class="text-[10px] text-slate-400 tabular-nums">
                  {{
                    formatTime(latestItem?.updated_at || latestItem?.created_at)
                  }}
                </div>
              </div>
            </div>
            <div class="text-right pl-2 hidden sm:block">
              <span
                class="px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold"
              >
                NEW
              </span>
            </div>
          </div>

          <!-- Color Balance Bar -->
          <div
            class="flex-1 min-w-[260px] bg-white border border-slate-200 rounded-xl p-3 shadow-xs flex flex-col justify-between h-auto"
          >
            <div
              class="flex items-center justify-between text-xs font-bold mb-1.5"
            >
              <span class="text-rose-600 flex items-center gap-1">
                <span
                  class="w-2.5 h-2.5 rounded-full bg-rose-600 inline-block shadow-xs"
                ></span>
                RED {{ stats.redPercent }}%
                <span class="text-[10px] font-normal text-slate-400"
                  >({{ stats.red }})</span
                >
              </span>
              <span class="text-emerald-600 font-extrabold text-[11px]">
                0: {{ stats.greenPercent }}%
                <span class="text-[10px] font-normal text-slate-400"
                  >({{ stats.green }})</span
                >
              </span>
              <span class="text-slate-800 flex items-center gap-1">
                <span
                  class="w-2.5 h-2.5 rounded-full bg-slate-800 inline-block shadow-xs"
                ></span>
                BLACK {{ stats.blackPercent }}%
                <span class="text-[10px] font-normal text-slate-400"
                  >({{ stats.black }})</span
                >
              </span>
            </div>

            <!-- Segmented Percentage Bar -->
            <div
              class="w-full h-3 bg-slate-100 rounded-full overflow-hidden flex border border-slate-200 p-0.5 gap-0.5"
            >
              <div
                class="bg-gradient-to-r from-rose-600 to-rose-500 rounded-l-full transition-all duration-500"
                :style="{ width: `${stats.redPercent}%` }"
                :title="`Red: ${stats.red} (${stats.redPercent}%)`"
              ></div>
              <div
                v-if="stats.green > 0"
                class="bg-emerald-500 transition-all duration-500"
                :style="{ width: `${stats.greenPercent}%` }"
                :title="`Zero: ${stats.green} (${stats.greenPercent}%)`"
              ></div>
              <div
                class="bg-gradient-to-r from-slate-700 to-slate-900 rounded-r-full transition-all duration-500"
                :style="{ width: `${stats.blackPercent}%` }"
                :title="`Black: ${stats.black} (${stats.blackPercent}%)`"
              ></div>
            </div>
          </div>

          <!-- Hot Numbers (Fit Content) -->
          <div
            class="shrink-0 w-auto bg-amber-50/80 border border-amber-200 rounded-xl p-3 flex flex-col justify-between shadow-xs"
          >
            <div
              class="text-[13px] font-extrabold uppercase text-amber-700 flex items-center justify-between gap-3"
            >
              <span class="flex items-center gap-1">🔥 Hot</span>
              <span class="text-[10px] font-bold text-amber-600/80">Top 5</span>
            </div>
            <div class="flex items-center gap-1.5 mt-1.5">
              <span
                v-for="h in hotNumbers.slice(0, 5)"
                :key="h.num"
                :class="[
                  'roulette-chip chip-mini  !cursor-default  hover:scale-110 transition-transform',
                  getChipColorClass(h.num),
                ]"
                :title="`Number ${h.num}: ${h.count} times  `"
              >
                {{ h.num }}
              </span>
            </div>
          </div>

          <!-- Cold Numbers (Fit Content) -->
          <div
            class="shrink-0 w-auto bg-sky-50/80 border border-sky-200 rounded-xl p-3 flex flex-col justify-between shadow-xs"
          >
            <div
              class="text-[13px] font-extrabold uppercase text-sky-700 flex items-center justify-between gap-3"
            >
              <span class="flex items-center gap-1">❄️ Cold</span>
              <span class="text-[10px] font-bold text-sky-600/80">Top 5</span>
            </div>
            <div class="flex items-center gap-1.5 mt-1.5">
              <span
                v-for="c in coldNumbers.slice(0, 5)"
                :key="c.num"
                :class="[
                  'roulette-chip chip-mini opacity-90 !cursor-default  hover:scale-110 transition-transform',
                  getChipColorClass(c.num),
                ]"
                :title="`Number ${c.num}: ${c.count} times  `"
              >
                {{ c.num }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- VIEW 1: GRID MODE -->
      <div v-if="activeView === 'grid'">
        <!-- Filter Toolbar -->
        <div
          class="p-3 sm:px-5 py-2.5 bg-white border-b border-slate-100 flex items-center justify-between"
        >
          <!-- Quick Filter Pills -->
          <div class="flex flex-wrap items-center gap-1.5">
            <button
              v-for="filter in filterOptions"
              :key="filter.id"
              @click="activeFilter = filter.id"
              :class="[
                'filter-pill',
                activeFilter === filter.id ? 'active' : '',
              ]"
            >
              <span
                v-if="filter.colorDot"
                :class="['dot-indicator', filter.colorDot]"
              ></span>
              {{ filter.label }}
            </button>
          </div>

          <span
            class="text-xs text-slate-400 font-semibold hidden sm:inline-block"
          >
            Showing {{ filteredNumbers.length }} spins
          </span>
        </div>

        <!-- Main Direct Grid View -->
        <div class="p-4 sm:p-5 bg-white">
          <!-- Loading Spinner -->
          <div
            v-if="isLoading || isRefreshing"
            class="flex flex-col items-center justify-center py-16 min-h-[260px] w-full"
          >
            <Loader />
            <span
              class="text-xs font-bold text-slate-400 mt-3 tracking-wider uppercase"
            >
              {{
                isRefreshing
                  ? "Refreshing live roulette extractions..."
                  : "Loading live roulette extractions..."
              }}
            </span>
          </div>

          <!-- Grid Container with Compact Uniform Chips -->
          <div v-else-if="filteredNumbers.length > 0" class="space-y-2">
            <div class="chips-grid-container scrollbar-thin">
              <div class="chips-fluid-grid">
                <div
                  v-for="(item, index) in filteredNumbers"
                  :key="item._id || item.roundId || index"
                  @click="openRoundDetail(item)"
                  class="chip-wrapper cursor-pointer"
                  :title="getTooltipText(item, index)"
                >
                  <!-- 3D Glossy Chip -->
                  <div
                    :class="[
                      'roulette-chip chip-grid',
                      getChipColorClass(item.multiplier),
                      index === 0 ? 'chip-latest-glow' : '',
                    ]"
                  >
                    <span class="chip-number">{{ item.multiplier }}</span>
                    <span v-if="index === 0" class="latest-dot-tag"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State (No spins matching filter or no data) -->
          <div
            v-else-if="
              !isLoading && !isRefreshing && filteredNumbers.length === 0
            "
            class="flex flex-col items-center justify-center py-8 px-4 text-center"
          >
            <div
              class="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-3"
            >
              <i
                v-if="activeFilter !== 'all'"
                class="fa-solid fa-filter-circle-xmark text-2xl text-slate-400"
              ></i>
              <i v-else class="fa-solid fa-dice text-2xl text-slate-400"></i>
            </div>

            <div v-if="activeFilter !== 'all'">
              <h4 class="text-sm font-bold text-slate-800 m-0">
                No spins found for "{{ getActiveFilterLabel }}"
              </h4>
              <p class="text-xs text-slate-400 m-0 mt-1 mb-4 max-w-sm">
                None of the recent {{ numbersList.length }} extractions match
                this filter.
              </p>
              <button
                @click="activeFilter = 'all'"
                class="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-all shadow-sm active:scale-95 cursor-pointer"
              >
                Reset Filter (Show All {{ numbersList.length }})
              </button>
            </div>

            <div v-else>
              <h4 class="text-sm font-bold text-slate-800 m-0">
                No extractions found for today
              </h4>
              <p class="text-xs text-slate-400 m-0 mt-1">
                Waiting for real-time table spins...
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- VIEW 2: BEAUTIFUL DETAILED STATISTICS VIEW -->
      <div
        v-else-if="activeView === 'stats'"
        class="p-4 sm:p-6 bg-white space-y-6"
      >
        <!-- Loading Spinner for Statistics -->
        <div
          v-if="isLoading || isRefreshing"
          class="flex flex-col items-center justify-center py-16 min-h-[260px] w-full"
        >
          <Loader />
          <span
            class="text-xs font-bold text-slate-400 mt-3 tracking-wider uppercase"
          >
            {{
              isRefreshing
                ? "Refreshing statistics..."
                : "Loading statistics..."
            }}
          </span>
        </div>

        <!-- Empty State for Statistics -->
        <div
          v-else-if="numbersList.length === 0"
          class="flex flex-col items-center justify-center py-8 px-4 text-center"
        >
          <div
            class="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-3"
          >
            <i class="fa-solid fa-chart-pie text-2xl text-slate-400"></i>
          </div>
          <h4 class="text-sm font-bold text-slate-800 m-0">
            No statistics available
          </h4>
          <p class="text-xs text-slate-400 m-0 mt-1">
            Waiting for real-time table extractions to compute statistics...
          </p>
        </div>

        <!-- Detailed Statistics Content -->
        <template v-else>
          <!-- Row 1: Dozens, Columns, Simple Chances -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Dozens Breakdown Card -->
            <div
              class="bg-slate-50/90 border border-slate-200 rounded-2xl p-4 shadow-xs"
            >
              <div class="flex items-center justify-between mb-3.5">
                <h4
                  class="text-xs font-extrabold uppercase tracking-wider text-slate-700 m-0 flex items-center gap-1.5"
                >
                  <i class="fa-solid fa-layer-group text-emerald-600"></i>
                  Dozens Breakdown
                </h4>
                <span class="text-[11px] font-bold text-slate-400"
                  >Total: {{ numbersList.length }}</span
                >
              </div>

              <div class="space-y-3.5">
                <!-- 1st 12 -->
                <div>
                  <div class="flex justify-between text-xs font-bold mb-1">
                    <span class="text-slate-700">1st 12 (1 – 12)</span>
                    <span class="text-emerald-700 font-extrabold">
                      {{ stats.dozen1 }}
                      <span class="text-[10px] text-slate-400 font-normal"
                        >({{ stats.dozen1Percent }}%)</span
                      >
                    </span>
                  </div>
                  <div class="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      :style="{ width: `${stats.dozen1Percent}%` }"
                    ></div>
                  </div>
                </div>

                <!-- 2nd 12 -->
                <div>
                  <div class="flex justify-between text-xs font-bold mb-1">
                    <span class="text-slate-700">2nd 12 (13 – 24)</span>
                    <span class="text-emerald-700 font-extrabold">
                      {{ stats.dozen2 }}
                      <span class="text-[10px] text-slate-400 font-normal"
                        >({{ stats.dozen2Percent }}%)</span
                      >
                    </span>
                  </div>
                  <div class="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      :style="{ width: `${stats.dozen2Percent}%` }"
                    ></div>
                  </div>
                </div>

                <!-- 3rd 12 -->
                <div>
                  <div class="flex justify-between text-xs font-bold mb-1">
                    <span class="text-slate-700">3rd 12 (25 – 36)</span>
                    <span class="text-emerald-700 font-extrabold">
                      {{ stats.dozen3 }}
                      <span class="text-[10px] text-slate-400 font-normal"
                        >({{ stats.dozen3Percent }}%)</span
                      >
                    </span>
                  </div>
                  <div class="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-emerald-500 rounded-full transition-all duration-500"
                      :style="{ width: `${stats.dozen3Percent}%` }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Columns Breakdown Card -->
            <div
              class="bg-slate-50/90 border border-slate-200 rounded-2xl p-4 shadow-xs"
            >
              <div class="flex items-center justify-between mb-3.5">
                <h4
                  class="text-xs font-extrabold uppercase tracking-wider text-slate-700 m-0 flex items-center gap-1.5"
                >
                  <i class="fa-solid fa-table-columns text-indigo-600"></i>
                  Columns Breakdown
                </h4>
                <span class="text-[11px] font-bold text-slate-400"
                  >12 numbers each</span
                >
              </div>

              <div class="space-y-3.5">
                <!-- 1st Column -->
                <div>
                  <div class="flex justify-between text-xs font-bold mb-1">
                    <span class="text-slate-700">1st Col (1, 4, 7... 34)</span>
                    <span class="text-indigo-700 font-extrabold">
                      {{ stats.col1 }}
                      <span class="text-[10px] text-slate-400 font-normal"
                        >({{ stats.col1Percent }}%)</span
                      >
                    </span>
                  </div>
                  <div class="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-indigo-500 rounded-full transition-all duration-500"
                      :style="{ width: `${stats.col1Percent}%` }"
                    ></div>
                  </div>
                </div>

                <!-- 2nd Column -->
                <div>
                  <div class="flex justify-between text-xs font-bold mb-1">
                    <span class="text-slate-700">2nd Col (2, 5, 8... 35)</span>
                    <span class="text-indigo-700 font-extrabold">
                      {{ stats.col2 }}
                      <span class="text-[10px] text-slate-400 font-normal"
                        >({{ stats.col2Percent }}%)</span
                      >
                    </span>
                  </div>
                  <div class="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-indigo-500 rounded-full transition-all duration-500"
                      :style="{ width: `${stats.col2Percent}%` }"
                    ></div>
                  </div>
                </div>

                <!-- 3rd Column -->
                <div>
                  <div class="flex justify-between text-xs font-bold mb-1">
                    <span class="text-slate-700">3rd Col (3, 6, 9... 36)</span>
                    <span class="text-indigo-700 font-extrabold">
                      {{ stats.col3 }}
                      <span class="text-[10px] text-slate-400 font-normal"
                        >({{ stats.col3Percent }}%)</span
                      >
                    </span>
                  </div>
                  <div class="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-indigo-500 rounded-full transition-all duration-500"
                      :style="{ width: `${stats.col3Percent}%` }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Chances Breakdown Card -->
            <div
              class="bg-slate-50/90 border border-slate-200 rounded-2xl p-4 shadow-xs"
            >
              <div class="flex items-center justify-between mb-3.5">
                <h4
                  class="text-xs font-extrabold uppercase tracking-wider text-slate-700 m-0 flex items-center gap-1.5"
                >
                  <i class="fa-solid fa-scale-balanced text-amber-600"></i> Even
                  Chances
                </h4>
                <span class="text-[11px] font-bold text-slate-400"
                  >1:1 Payouts</span
                >
              </div>

              <div class="space-y-3.5">
                <!-- High / Low -->
                <div>
                  <div class="flex justify-between text-xs font-bold mb-1">
                    <span class="text-slate-700">1–18 (Low)</span>
                    <span class="text-slate-700">19–36 (High)</span>
                  </div>
                  <div
                    class="flex justify-between text-[10px] text-slate-400 mb-1"
                  >
                    <span>{{ stats.low }} ({{ stats.lowPercent }}%)</span>
                    <span>{{ stats.high }} ({{ stats.highPercent }}%)</span>
                  </div>
                  <div
                    class="h-2.5 bg-slate-200 rounded-full overflow-hidden flex gap-0.5 p-0.5"
                  >
                    <div
                      class="bg-blue-500 h-full rounded-l-full"
                      :style="{ width: `${stats.lowPercent}%` }"
                    ></div>
                    <div
                      class="bg-purple-500 h-full rounded-r-full"
                      :style="{ width: `${stats.highPercent}%` }"
                    ></div>
                  </div>
                </div>

                <!-- Even / Odd -->
                <div>
                  <div class="flex justify-between text-xs font-bold mb-1">
                    <span class="text-slate-700">Even</span>
                    <span class="text-slate-700">Odd</span>
                  </div>
                  <div
                    class="flex justify-between text-[10px] text-slate-400 mb-1"
                  >
                    <span>{{ stats.even }} ({{ stats.evenPercent }}%)</span>
                    <span>{{ stats.odd }} ({{ stats.oddPercent }}%)</span>
                  </div>
                  <div
                    class="h-2.5 bg-slate-200 rounded-full overflow-hidden flex gap-0.5 p-0.5"
                  >
                    <div
                      class="bg-amber-500 h-full rounded-l-full"
                      :style="{ width: `${stats.evenPercent}%` }"
                    ></div>
                    <div
                      class="bg-teal-500 h-full rounded-r-full"
                      :style="{ width: `${stats.oddPercent}%` }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Row 2: Hot & Cold Ranked Cards -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Top 5 Hot Numbers -->
            <div
              class="bg-amber-50/70 border border-amber-200/80 rounded-2xl p-4"
            >
              <div class="flex items-center justify-between mb-3">
                <h4
                  class="text-xs font-extrabold uppercase tracking-wider text-amber-800 m-0 flex items-center gap-1.5"
                >
                  🔥 Top 5 Hot Numbers (Most Drawn)
                </h4>
                <span class="text-[11px] font-bold text-amber-700"
                  >Highest Frequency</span
                >
              </div>

              <div class="grid grid-cols-5 gap-2">
                <div
                  v-for="(h, idx) in hotNumbers.slice(0, 5)"
                  :key="h.num"
                  @click="openLatestRoundForNumber(h.num)"
                  class="bg-white border border-amber-200 rounded-xl p-2.5 flex flex-col items-center text-center shadow-xs cursor-pointer hover:border-amber-400 hover:shadow-sm transition-all"
                  :title="`Number ${h.num}: ${h.count} times (Click to view round details)`"
                >
                  <span class="text-[9px] font-extrabold text-amber-600 mb-1"
                    >#{{ idx + 1 }}</span
                  >
                  <div
                    :class="[
                      'roulette-chip chip-mini mb-1.5',
                      getChipColorClass(h.num),
                    ]"
                  >
                    {{ h.num }}
                  </div>
                  <span class="text-xs font-black text-slate-800 tabular-nums"
                    >{{ h.count }}x</span
                  >
                  <span class="text-[9px] text-slate-400 font-semibold"
                    >{{ getFrequencyPercent(h.count) }}%</span
                  >
                </div>
              </div>
            </div>

            <!-- Top 5 Cold Numbers -->
            <div class="bg-sky-50/70 border border-sky-200/80 rounded-2xl p-4">
              <div class="flex items-center justify-between mb-3">
                <h4
                  class="text-xs font-extrabold uppercase tracking-wider text-sky-800 m-0 flex items-center gap-1.5"
                >
                  ❄️ Top 5 Cold Numbers (Least Drawn)
                </h4>
                <span class="text-[11px] font-bold text-sky-700"
                  >Lowest Frequency</span
                >
              </div>

              <div class="grid grid-cols-5 gap-2">
                <div
                  v-for="(c, idx) in coldNumbers.slice(0, 5)"
                  :key="c.num"
                  @click="openLatestRoundForNumber(c.num)"
                  class="bg-white border border-sky-200 rounded-xl p-2.5 flex flex-col items-center text-center shadow-xs cursor-pointer hover:border-sky-400 hover:shadow-sm transition-all"
                  :title="`Number ${c.num}: ${c.count} times (Click to view round details)`"
                >
                  <span class="text-[9px] font-extrabold text-sky-600 mb-1"
                    >#{{ idx + 1 }}</span
                  >
                  <div
                    :class="[
                      'roulette-chip chip-mini mb-1.5 opacity-90',
                      getChipColorClass(c.num),
                    ]"
                  >
                    {{ c.num }}
                  </div>
                  <span class="text-xs font-black text-slate-800 tabular-nums"
                    >{{ c.count }}x</span
                  >
                  <span class="text-[9px] text-slate-400 font-semibold"
                    >{{ getFrequencyPercent(c.count) }}%</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Row 3: Full Wheel Heatmap Matrix (0 - 36) -->
          <div
            class="bg-slate-50/90 border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-xs"
          >
            <div
              class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-200/80"
            >
              <div>
                <h4
                  class="text-sm font-extrabold text-slate-900 m-0 flex items-center gap-2"
                >
                  <i class="fa-solid fa-bullseye text-rose-600"></i> Full Wheel
                  Heatmap (0 – 36)
                </h4>
                <p class="text-xs text-slate-500 m-0 mt-0.5">
                  Detailed hit counter for all 37 roulette pocket positions
                  (Click any number to view round details)
                </p>
              </div>
              <div class="flex items-center gap-3 text-xs">
                <span
                  class="flex items-center gap-1.5 text-slate-600 font-semibold"
                >
                  <span
                    class="w-3 h-3 rounded-full bg-emerald-500 inline-block"
                  ></span>
                  Zero (0)
                </span>
                <span
                  class="flex items-center gap-1.5 text-slate-600 font-semibold"
                >
                  <span
                    class="w-3 h-3 rounded-full bg-rose-600 inline-block"
                  ></span>
                  Red (18)
                </span>
                <span
                  class="flex items-center gap-1.5 text-slate-600 font-semibold"
                >
                  <span
                    class="w-3 h-3 rounded-full bg-slate-800 inline-block"
                  ></span>
                  Black (18)
                </span>
              </div>
            </div>

            <!-- Heatmap Matrix Grid -->
            <div
              class="grid grid-cols-4 sm:grid-cols-7 md:grid-cols-10 lg:grid-cols-13 gap-2"
            >
              <div
                v-for="n in 37"
                :key="n - 1"
                @click="
                  getNumberCount(n - 1) > 0 && openLatestRoundForNumber(n - 1)
                "
                :class="[
                  'heatmap-card',
                  getNumberCount(n - 1) > 0
                    ? 'has-hits cursor-pointer hover:border-slate-400 hover:shadow-sm'
                    : 'zero-hits cursor-default',
                ]"
                :title="`Number ${n - 1}: ${getNumberCount(
                  n - 1,
                )} hits (${getFrequencyPercent(getNumberCount(n - 1))}%) ${
                  getNumberCount(n - 1) > 0
                    ? '• Click to view round details'
                    : ''
                }`"
              >
                <div
                  :class="['roulette-chip chip-mini', getChipColorClass(n - 1)]"
                >
                  {{ n - 1 }}
                </div>
                <div class="mt-1 flex flex-col items-center">
                  <span class="text-xs font-black text-slate-800 tabular-nums">
                    {{ getNumberCount(n - 1) }}x
                  </span>
                  <span
                    class="text-[9px] text-slate-400 font-semibold tabular-nums"
                  >
                    {{ getFrequencyPercent(getNumberCount(n - 1)) }}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Live Casino Round Detail Modal -->
  <Teleport to="body">
    <LiveCasinoRoundModal
      v-if="showRoundModal && selectedRoundInfo"
      :isVisible="showRoundModal"
      :roundInfo="selectedRoundInfo"
      :closeModal="closeRoundModal"
      title="Live Casino Round Details"
    />
  </Teleport>
</template>

<script>
import { getFetch } from "@/api/axiosClient";
import Loader from "@/components/Loader.vue";
import LiveCasinoRoundModal from "./LiveCasinoRoundModal.vue";

const RED_NUMBERS = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);
const BLACK_NUMBERS = new Set([
  2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35,
]);

export default {
  name: "LastRouletteNumber",
  components: {
    Loader,
    LiveCasinoRoundModal,
  },
  props: {
    limit: {
      type: Number,
      default: 500,
    },
  },
  data() {
    return {
      isLoading: true,
      isRefreshing: false,
      numbersList: [],
      pollingInterval: null,
      activeView: "grid", // 'grid' | 'stats'
      activeFilter: "all", // 'all' | 'red' | 'black' | 'green' | 'even' | 'odd' | 'low' | 'high'
      showRoundModal: false,
      selectedRoundInfo: null,
      filterOptions: [
        { id: "all", label: "All" },
        { id: "red", label: "Red", colorDot: "bg-rose-500" },
        { id: "black", label: "Black", colorDot: "bg-slate-800" },
        { id: "green", label: "0 Zero", colorDot: "bg-emerald-500" },
        { id: "even", label: "Even" },
        { id: "odd", label: "Odd" },
        { id: "low", label: "1-18" },
        { id: "high", label: "19-36" },
      ],
    };
  },
  computed: {
    getActiveFilterLabel() {
      const found = this.filterOptions.find((f) => f.id === this.activeFilter);
      return found ? found.label : this.activeFilter;
    },

    latestItem() {
      return this.numbersList[0] || null;
    },

    filteredNumbers() {
      let list = this.numbersList;

      // Filter by category
      if (this.activeFilter !== "all") {
        list = list.filter((item) => {
          const num = Number(item.multiplier);
          if (this.activeFilter === "green") return num === 0;
          if (this.activeFilter === "red") return RED_NUMBERS.has(num);
          if (this.activeFilter === "black") return BLACK_NUMBERS.has(num);
          if (this.activeFilter === "even") return num > 0 && num % 2 === 0;
          if (this.activeFilter === "odd") return num > 0 && num % 2 !== 0;
          if (this.activeFilter === "low") return num >= 1 && num <= 18;
          if (this.activeFilter === "high") return num >= 19 && num <= 36;
          return true;
        });
      }

      return list;
    },

    numberFrequencyMap() {
      const map = {};
      for (let i = 0; i <= 36; i++) map[i] = 0;
      for (const item of this.numbersList) {
        const n = Number(item.multiplier);
        if (n >= 0 && n <= 36) {
          map[n] = (map[n] || 0) + 1;
        }
      }
      return map;
    },

    hotNumbers() {
      const entries = Object.entries(this.numberFrequencyMap).map(
        ([num, count]) => ({
          num: Number(num),
          count,
        }),
      );
      return entries.sort((a, b) => b.count - a.count);
    },

    coldNumbers() {
      const entries = Object.entries(this.numberFrequencyMap).map(
        ([num, count]) => ({
          num: Number(num),
          count,
        }),
      );
      return entries.sort((a, b) => a.count - b.count);
    },

    stats() {
      const total = this.numbersList.length;
      if (!total) {
        return {
          red: 0,
          redPercent: 0,
          black: 0,
          blackPercent: 0,
          green: 0,
          greenPercent: 0,
          even: 0,
          evenPercent: 0,
          odd: 0,
          oddPercent: 0,
          low: 0,
          lowPercent: 0,
          high: 0,
          highPercent: 0,
          dozen1: 0,
          dozen1Percent: 0,
          dozen2: 0,
          dozen2Percent: 0,
          dozen3: 0,
          dozen3Percent: 0,
          col1: 0,
          col1Percent: 0,
          col2: 0,
          col2Percent: 0,
          col3: 0,
          col3Percent: 0,
        };
      }

      let red = 0;
      let black = 0;
      let green = 0;
      let even = 0;
      let odd = 0;
      let low = 0;
      let high = 0;
      let dozen1 = 0;
      let dozen2 = 0;
      let dozen3 = 0;
      let col1 = 0;
      let col2 = 0;
      let col3 = 0;

      for (const item of this.numbersList) {
        const num = Number(item.multiplier);
        if (num === 0) {
          green++;
        } else {
          if (RED_NUMBERS.has(num)) red++;
          if (BLACK_NUMBERS.has(num)) black++;
          if (num % 2 === 0) even++;
          else odd++;
          if (num >= 1 && num <= 18) low++;
          else if (num >= 19 && num <= 36) high++;
          if (num >= 1 && num <= 12) dozen1++;
          else if (num >= 13 && num <= 24) dozen2++;
          else if (num >= 25 && num <= 36) dozen3++;

          if (num % 3 === 1) col1++;
          else if (num % 3 === 2) col2++;
          else if (num % 3 === 0) col3++;
        }
      }

      return {
        red,
        redPercent: Math.round((red / total) * 100),
        black,
        blackPercent: Math.round((black / total) * 100),
        green,
        greenPercent: Math.round((green / total) * 100),
        even,
        evenPercent: Math.round((even / total) * 100),
        odd,
        oddPercent: Math.round((odd / total) * 100),
        low,
        lowPercent: Math.round((low / total) * 100),
        high,
        highPercent: Math.round((high / total) * 100),
        dozen1,
        dozen1Percent: Math.round((dozen1 / total) * 100),
        dozen2,
        dozen2Percent: Math.round((dozen2 / total) * 100),
        dozen3,
        dozen3Percent: Math.round((dozen3 / total) * 100),
        col1,
        col1Percent: Math.round((col1 / total) * 100),
        col2,
        col2Percent: Math.round((col2 / total) * 100),
        col3,
        col3Percent: Math.round((col3 / total) * 100),
      };
    },
  },
  methods: {
    getFrequencyPercent(count) {
      if (!this.numbersList.length || !count) return 0;
      return Math.round((count / this.numbersList.length) * 100);
    },
    getNumberCount(num) {
      return this.numberFrequencyMap[num] || 0;
    },
    getNumberDescriptor(val) {
      if (val === undefined || val === null) return "-";
      const num = Number(val);
      if (num === 0) return "Zero (Green)";
      const color = RED_NUMBERS.has(num)
        ? "Red"
        : BLACK_NUMBERS.has(num)
        ? "Black"
        : "";
      const parity = num % 2 === 0 ? "Even" : "Odd";
      const range = num <= 18 ? "1-18 (Low)" : "19-36 (High)";
      return `${color} • ${parity} • ${range}`;
    },
    getChipColorClass(val) {
      const num = Number(val);
      if (num === 0) {
        return "chip-color-green";
      }
      if (RED_NUMBERS.has(num)) {
        return "chip-color-red";
      }
      if (BLACK_NUMBERS.has(num)) {
        return "chip-color-black";
      }
      return "chip-color-neutral";
    },
    formatTime(time) {
      if (!time) return "-";
      const d = new Date(time);
      return d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    formatDateTime(time) {
      if (!time) return "-";
      const d = new Date(time);
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
    },
    getTooltipText(item, index) {
      const time = item.updated_at || item.created_at || item.end_at;
      const formattedTime = time ? new Date(time).toLocaleTimeString() : "";
      const prefix = index === 0 ? "Latest: " : "Number: ";
      return `${prefix}${item.multiplier}${
        formattedTime ? ` (${formattedTime})` : ""
      }${item.roundId ? ` | Round: ${item.roundId.slice(0, 8)}...` : ""}`;
    },
    async handleManualRefresh() {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      const startTime = Date.now();
      try {
        await this.fetchMultipliers(false);
      } finally {
        const elapsed = Date.now() - startTime;
        const minSpinDuration = 500;
        if (elapsed < minSpinDuration) {
          setTimeout(() => {
            this.isRefreshing = false;
          }, minSpinDuration - elapsed);
        } else {
          this.isRefreshing = false;
        }
      }
    },
    async fetchMultipliers(showLoader = false) {
      if (showLoader && this.numbersList.length === 0) {
        this.isLoading = true;
      }
      try {
        const today = new Date();
        const startOfDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          0,
          0,
          0,
          0,
        ).toISOString();
        const endOfDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          23,
          59,
          0,
          0,
        ).toISOString();

        const params = {
          page: 1,
          limit: this.limit || 500,
          sortField: "updated_at",
          sortOrder: "desc",
          start_date: startOfDay,
          end_date: endOfDay,
          type: "roulette",
        };

        let provider = process.env.VUE_APP_PROVIDER ?? "theearcasino";

        const response = await getFetch(
          "multipliers",
          params,
          true,
          `${provider}_livecasino`,
        );

        const data =
          response?.data ||
          response?.items ||
          response?.multipliers ||
          response ||
          [];
        const rawList = Array.isArray(data)
          ? data
          : data?.multipliers && Array.isArray(data.multipliers)
          ? data.multipliers
          : [];
        this.numbersList = rawList.filter(
          (item) =>
            item?.multiplier !== undefined &&
            item?.multiplier !== null &&
            item?.multiplier !== "" &&
            Number(item.multiplier) !== 999,
        );
      } catch (error) {
        console.error("Error fetching last roulette numbers:", error);
      } finally {
        this.isLoading = false;
      }
    },
    openRoundDetail(item) {
      if (!item) return;
      this.selectedRoundInfo = item;
      this.showRoundModal = true;
    },
    closeRoundModal() {
      this.showRoundModal = false;
      this.selectedRoundInfo = null;
    },
    openLatestRoundForNumber(num) {
      if (num === null || num === undefined) return;
      const targetNum = Number(num);
      const found = this.numbersList.find(
        (item) => Number(item.multiplier) === targetNum,
      );
      if (found) {
        this.openRoundDetail(found);
      }
    },
  },
  async mounted() {
    await this.fetchMultipliers(true);
    // Polling every 10 seconds for real-time roulette feed
    this.pollingInterval = setInterval(() => {
      this.fetchMultipliers(false);
    }, 10000);
  },
  beforeUnmount() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  },
};
</script>

<style scoped>
/* Roulette Component Light Theme Styles */
.roulette-monitor-wrapper {
  width: 100%;
}

.roulette-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Header icon badge */
.roulette-icon-badge {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, #10b981 0%, #047857 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.25);
  flex-shrink: 0;
}

.live-pulse-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background-color: #10b981;
  border: 2px solid #ffffff;
}

/* Live Badge */
.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  background-color: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}

.pulse-ring {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background-color: #10b981;
  animation: pulse 1.5s infinite;
}

/* Switcher Buttons in Light Theme */
.view-switcher-group {
  display: flex;
  align-items: center;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.switch-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.switch-btn:hover {
  color: #0f172a;
}

.switch-btn.active {
  background: var(--primary-color, #386f99);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(5, 150, 105, 0.3);
}

.refresh-btn {
  padding: 8px 12px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover {
  color: #0f172a;
  border-color: #cbd5e1;
  background: #f8fafc;
}

/* Filter Pills */
.filter-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}

.filter-pill:hover {
  border-color: #cbd5e1;
  color: #0f172a;
  background: #f8fafc;
}

.filter-pill.active {
  background: #0f172a;
  border-color: #0f172a;
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(15, 23, 42, 0.15);
}

.dot-indicator {
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  margin-right: 5px;
}

/* =========================================
   3D LUXURY ROULETTE CHIPS
========================================= */
.roulette-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-family: inherit;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.18s ease;
  box-sizing: border-box;
}

.roulette-chip:hover {
  transform: scale(1.03) translateY(-2px);
  z-index: 30;
}

.chip-number {
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.65);
}

/* Size Variants */
.chip-grid {
  width: 35px;
  height: 35px;
  font-size: 12px;
}

@media (min-width: 640px) {
  .chip-grid {
    width: 30px;
    height: 30px;
    font-size: 13px;
  }
}

.chip-hero {
  width: 48px;
  height: 48px;
  font-size: 20px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
}

.chip-mini {
  width: 28px;
  height: 28px;
  font-size: 11px;
}

.chip-modal {
  width: 60px;
  height: 60px;
  font-size: 26px;
}

/* RED ROULETTE CHIP */
.chip-color-red {
  background: radial-gradient(
    circle at 35% 30%,
    #ff5252 0%,
    #e11d48 55%,
    #9f1239 100%
  );
  border: 1.5px solid #fecdd3;
  color: #ffffff;
  box-shadow: 0 3px 6px rgba(225, 29, 72, 0.28),
    inset 0 2px 3px rgba(255, 255, 255, 0.45),
    inset 0 -2px 3px rgba(0, 0, 0, 0.3);
}

.chip-color-red:hover {
  box-shadow: 0 6px 16px rgba(225, 29, 72, 0.5),
    inset 0 2px 3px rgba(255, 255, 255, 0.5);
}

/* BLACK ROULETTE CHIP */
.chip-color-black {
  background: radial-gradient(
    circle at 35% 30%,
    #475569 0%,
    #1e293b 65%,
    #0f172a 100%
  );
  border: 1.5px solid #94a3b8;
  color: #ffffff;
  box-shadow: 0 3px 6px rgba(15, 23, 42, 0.3),
    inset 0 2px 3px rgba(255, 255, 255, 0.3),
    inset 0 -2px 3px rgba(0, 0, 0, 0.45);
}

.chip-color-black:hover {
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.55),
    inset 0 2px 3px rgba(255, 255, 255, 0.4);
}

/* ZERO GREEN CHIP */
.chip-color-green {
  background: radial-gradient(
    circle at 35% 30%,
    #34d399 0%,
    #059669 60%,
    #064e3b 100%
  );
  border: 1.5px solid #a7f3d0;
  color: #ffffff;
  box-shadow: 0 3px 6px rgba(5, 150, 105, 0.35),
    inset 0 2px 3px rgba(255, 255, 255, 0.45),
    inset 0 -2px 3px rgba(0, 0, 0, 0.3);
}

.chip-color-green:hover {
  box-shadow: 0 6px 16px rgba(5, 150, 105, 0.6),
    inset 0 2px 3px rgba(255, 255, 255, 0.6);
}

.chip-color-neutral {
  background: radial-gradient(
    circle at 35% 30%,
    #818cf8 0%,
    #4f46e5 60%,
    #312e81 100%
  );
  border: 1.5px solid #c7d2fe;
  color: #ffffff;
  box-shadow: 0 3px 6px rgba(79, 70, 229, 0.3);
}

/* Latest chip highlights */
.chip-latest-glow {
  outline: 3px solid #10b981;
  outline-offset: 2px;
  animation: bounceGlowLight 2.5s infinite ease-in-out;
}

@keyframes bounceGlowLight {
  0%,
  100% {
    outline-color: #10b981;
    box-shadow: 0 0 12px rgba(16, 185, 129, 0.45);
  }
  50% {
    outline-color: #059669;
    box-shadow: 0 0 18px rgba(5, 150, 105, 0.7);
  }
}

.latest-dot-tag {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: #10b981;
  border: 1.5px solid #ffffff;
  box-shadow: 0 0 5px #10b981;
}

/* Grid Layout container */
.chips-grid-container {
  max-height: 520px;
  overflow-y: auto;
  padding: 8px 4px;
}

.chips-fluid-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
}

.chip-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Heatmap Cards */
.heatmap-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.heatmap-card:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.heatmap-card.zero-hits {
  opacity: 0.55;
  background: #f8fafc;
}

.heatmap-card.has-hits {
  opacity: 1;
}

/* Scrollbar Customization for Light Theme */
.scrollbar-thin::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 9999px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 9999px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
